import React, { useState, useEffect, useMemo } from 'react';
import { Card, SuggestedPhrases, CardConsequence } from '../../types';
import TypingAnimation from '../TypingAnimation';
import { useSoundEffects } from '../../context/SoundEffectsContext';
import PlayableCard from './PlayableCard';
import './CardDetailModal.css';

interface CardDetailModalProps {
  card: Card;
  suggestedPhrases: SuggestedPhrases;
  isLoading: boolean;
  onClose: () => void;
  onPlayCard: (cardName: string, actionPhrase: string) => void;
  isInFreeFormStep?: boolean;
  isTutorialMode?: boolean;
  tutorialStep?: number;
  onTypingComplete?: () => void;
  isAnimatingAsterisk?: boolean;
}

const formatCardName = (name: string): string => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CardDetailModal: React.FC<CardDetailModalProps> = ({
  card,
  suggestedPhrases,
  isLoading,
  onClose,
  onPlayCard,
  isInFreeFormStep = true,
  isTutorialMode = false,
  tutorialStep,
  onTypingComplete,
  isAnimatingAsterisk = false,
}) => {
  const [selectedPhrase, setSelectedPhrase] = useState<string>('');
  const [customPhrase, setCustomPhrase] = useState<string>('');
  const [isVisible, setIsVisible] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [tutorialPredefinedPhrase, setTutorialPredefinedPhrase] = useState<string>('');
  const { playSound } = useSoundEffects();

  const isChaosCard = card.type === 'chaos';
  const isAllyCard = card.type === 'ally';
  const phrases = useMemo(
    () => suggestedPhrases.card_phrases[card.name] || [],
    [suggestedPhrases.card_phrases, card.name]
  );

  // Tutorial step 4: Set predefined phrase for auto-typing
  useEffect(() => {
    if (isTutorialMode && tutorialStep === 4 && isInFreeFormStep) {
      if (phrases.length === 1) {
        setTutorialPredefinedPhrase(phrases[0]);
        setIsTypingComplete(false);
      }
    } else {
      setTutorialPredefinedPhrase('');
      setIsTypingComplete(false);
    }
  }, [isTutorialMode, tutorialStep, isInFreeFormStep, phrases]);

  const handleTypingComplete = () => {
    setIsTypingComplete(true);
    setCustomPhrase(tutorialPredefinedPhrase);
    if (onTypingComplete) {
      onTypingComplete();
    }
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  const handleClose = () => {
    playSound('ui_window_close');
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handlePhraseSelect = (phrase: string) => {
    playSound('typing');
    setSelectedPhrase(phrase);
    setCustomPhrase('');
    // Auto-submit when a suggested phrase is selected
    onPlayCard(card.name, phrase);
  };

  const handleCustomPhraseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomPhrase(e.target.value);
    setSelectedPhrase('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePlayCard();
    }
  };

  const handlePlayCard = () => {
    playSound('press_enter');
    if (isChaosCard) {
      onPlayCard(card.name, '');
      return;
    }

    if (!selectedPhrase && !customPhrase) return;

    let finalActionPhrase = selectedPhrase;

    if (!selectedPhrase && customPhrase) {
      if (isAllyCard && card.ally_name && card.verb) {
        finalActionPhrase = `I ask ${card.ally_name} to ${card.verb} ${customPhrase}`;
      } else {
        finalActionPhrase = `I ${card.verb} ${customPhrase}`;
      }
    }

    onPlayCard(card.name, finalActionPhrase);
  };

  const getInputPrefix = (): string => {
    if (isAllyCard && card.ally_name && card.verb) {
      return `I ask ${card.ally_name} to ${card.verb} `;
    }
    return `I ${card.verb || ''} `;
  };

  // In tutorial step 4, user must wait for typing to complete before playing
  const isTutorialTypingStep = isTutorialMode && tutorialStep === 4 && !!tutorialPredefinedPhrase;
  const canPlay = isChaosCard || selectedPhrase || customPhrase || (isTutorialTypingStep && isTypingComplete);

  return (
    <div className={`card-detail-overlay ${isVisible ? 'visible' : ''}`} onClick={handleClose}>
      <div
        className={`card-detail-modal ${isVisible ? 'visible' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="card-detail-back" onClick={handleClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6,9 12,15 18,9" />
          </svg>
          <span>Back</span>
        </button>

        <div className="card-detail-content">
          <div className="card-detail-top">
            <PlayableCard
              card={card}
              className="card-detail-preview"
              animatingIndex={isAnimatingAsterisk ? (card.usage_count ?? 0) : undefined}
            />
            <div className="card-detail-info">
              <h2 className="card-detail-title">{formatCardName(card.name)}</h2>
              {card.description && (
                <p className="card-detail-description">{card.description}</p>
              )}
              {isChaosCard && !card.description && (
                <p className="card-detail-description card-detail-chaos-text">
                  Play this chaos card to introduce an unexpected twist!
                </p>
              )}
              {/* Card consequences (bonuses and penalties) */}
              {card.consequences && card.consequences.length > 0 && (
                <div className="card-detail-consequences">
                  {card.consequences.map((c: CardConsequence, i: number) => (
                    <span
                      key={i}
                      className={`card-detail-consequence ${c.is_positive === false ? 'negative' : 'positive'}`}
                    >
                      {c.description}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card-detail-bottom">
            {!isChaosCard && (
              <>
                {!(isTutorialMode && tutorialStep === 4) && (
                  <div className="card-detail-section card-detail-suggestions">
                    <h3 className="card-detail-section-title">Suggested Actions</h3>
                    <div className="card-detail-phrases">
                      {phrases.length > 0 ? (
                        phrases.map((phrase, index) => (
                          <button
                            key={index}
                            className={`card-detail-phrase ${selectedPhrase === phrase ? 'selected' : ''}`}
                            onClick={() => handlePhraseSelect(phrase)}
                            disabled={isLoading}
                          >
                            {phrase}
                          </button>
                        ))
                      ) : (
                        <p className="card-detail-no-phrases">No suggestions available</p>
                      )}
                    </div>
                  </div>
                )}

                {isInFreeFormStep && (
                  <div className="card-detail-section">
                    <h3 className="card-detail-section-title">Or type your own:</h3>
                    <div className="card-detail-input-wrapper">
                      <span className="card-detail-input-prefix">{getInputPrefix()}</span>
                      {/* Tutorial step 4: Show typing animation */}
                      {isTutorialMode && tutorialStep === 4 && tutorialPredefinedPhrase ? (
                        <div className="card-detail-typing-container">
                          <TypingAnimation
                            text={tutorialPredefinedPhrase}
                            speed={120}
                            onComplete={handleTypingComplete}
                            showCursor={true}
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          className="card-detail-input"
                          value={customPhrase}
                          onChange={handleCustomPhraseChange}
                          onKeyDown={handleKeyDown}
                          placeholder="type your action..."
                          disabled={isLoading}
                          maxLength={250}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <button
              className={`card-detail-play-btn ${isChaosCard ? 'chaos' : ''}`}
              onClick={handlePlayCard}
              disabled={isLoading || !canPlay || (isTutorialTypingStep && !isTypingComplete)}
            >
              {isLoading ? 'Loading...' : isChaosCard ? 'Unleash Chaos!' : 'Play Card'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardDetailModal;
